import { FC, useLayoutEffect, useRef, useState } from "react";

import { Breadcrumb } from "../../../components/Breadcrumb/Breadcrumb";
import { Container } from "../../../components/Container/Container";
import { ContentBuilder } from "../../../components/ContentBuilder/ContentBuilder";
import { ContentFooter } from "../../../components/ContentFooter/ContentFooter";
import { Seo } from "../../../components/Seo/Seo";
import { usePageContent } from "../../../hooks/content/usePageContent";
import { useRouteInfo } from "../../../hooks/route/useRouteInfo";
import { useWindowSize } from "../../../hooks/useWindowSize";
import { UiBreakpoints } from "../../../config/ui/breakpoints";
import { MarginaliaProvider, useMarginaliaContext } from "../../../components/ContentBuilder/MarginaliaContext/MarginaliaContext";

const AsideContent: FC<{ blocks: ReturnType<typeof usePageContent>["blocks"] }> = ({ blocks }) => {
	const { blockRefs } = useMarginaliaContext();
	const asideRef = useRef<HTMLDivElement>(null);
	const [positions, setPositions] = useState<Record<string, number>>({});

	useLayoutEffect(() => {
		const compute = () => {
			if (!asideRef.current) return;
			const asideRect = asideRef.current.getBoundingClientRect();
			const next: Record<string, number> = {};
			for (const { id, el } of blockRefs) {
				const blockRect = el.getBoundingClientRect();
				next[id] = blockRect.top - asideRect.top;
			}
			setPositions(next);
		};

		compute();

		const observer = new ResizeObserver(compute);
		if (asideRef.current) observer.observe(asideRef.current);
		for (const { el } of blockRefs) observer.observe(el);

		return () => observer.disconnect();
	}, [blockRefs]);

	const marginaliaBlocks = blocks.filter((block) => block.marginalia);

	return (
		<div ref={asideRef} className="relative w-full">
			{marginaliaBlocks.map((block) => {
				const MarginaliaComponent = block.marginalia!.component;
				const top = positions[block.id];
				return (
					<div
						key={block.id}
						style={{ position: "absolute", top: top ?? 0, left: 0, right: 0 }}
					>
						<MarginaliaComponent {...(block.marginalia!.props ?? {})} />
					</div>
				);
			})}
		</div>
	);
};

const IntroductionDetail: FC = () => {
	const { routeData, title, description, routeParent } = useRouteInfo();
	const { width } = useWindowSize();
	const sidebarCollapsed = width < UiBreakpoints.lg;
	const { blocks, pageIntro, pageMarginalia } = usePageContent();

	return (
		<MarginaliaProvider inline={sidebarCollapsed}>
			<>
				<Seo
					title={title}
					description={typeof description === "string" && description.trim().length > 0 ? description : undefined}
				/>

				<div className="w-full flex items-center p-6 md:px-9 xl:px-14 bg-white border-b border-gray-200">
					<Breadcrumb
						items={[
							{ label: "Home", to: "/", className: "text-secondary" },
							{ label: routeParent?.label ?? "", to: routeParent?.url, className: "text-gray-400" },
							{ label: routeData?.label ?? "", className: "text-gray-800" },
						]}
					/>
				</div>

				<article className="break-words flex">
					<div className="w-full min-w-0" style={{ maxWidth: 1020 }}>
						<Container contentPaddingClasses="px-6 pt-8 pb-0 md:px-9 md:pt-9 xl:px-14 xl:pt-14">
							<div className="flex flex-col gap-1 w-full max-w-6xl mx-auto">
								<p className="text-primary uppercase font-light tracking-wide">
									{routeParent?.label ?? ""}
								</p>
								<h1 className="text-3xl font-black text-black">{routeData?.label ?? ""}</h1>
							</div>
						</Container>

						<Container contentClassName="!py-0">
							<div className="w-full max-w-6xl mx-auto">
								{typeof pageIntro !== "undefined" ? <p className="text-xl leading-relaxed">{pageIntro}</p> : null}
								<ContentBuilder blocks={blocks} />
								<ContentFooter />
							</div>
						</Container>
					</div>

					{!sidebarCollapsed && (
						<aside style={{ flex: 1, minWidth: 240 }} className="bg-gray-100">
							<div className="p-6" style={{ maxWidth: 450 }}>
								{pageMarginalia && (() => {
									const PageMarginaliaComponent = pageMarginalia.component;
									return (
										<div className="mb-8">
											<PageMarginaliaComponent {...(pageMarginalia.props ?? {})} />
										</div>
									);
								})()}
								<AsideContent blocks={blocks} />
							</div>
						</aside>
					)}
				</article>
			</>
		</MarginaliaProvider>
	);
};

export { IntroductionDetail };
